import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Spinner, Alert, Button } from "reactstrap";
import MetaTags from "react-meta-tags";
import "../Findings/CentralFindings.scss";
import "./redTeam.scss";
import { STATUS_LABELS, fmt, Pill, ApprovalsTab, ActivityTab, GapsTab, FindingsTab, ReportTab } from "./RedTeamTabs";

const TABS = [
  ["plan", "Plan"],
  ["approvals", "Approvals"],
  ["activity", "Activity log"],
  ["gaps", "Gaps & actions"],
  ["findings", "Findings in scope"],
  ["report", "Report"],
];
const BLANK_CONTACT = { name: "", role: "", phone: "", email: "" };
const EMPTY_PLAN = { name: "", objective: "", scenarios: [], rulesOfEngagement: "", scope: "", exclusions: "", start: "", end: "", emergencyContacts: [{ ...BLANK_CONTACT }], stopConditions: "" };

const toLocalInput = (value) => {
  const d = new Date(value);
  if (!value || Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const planFromEngagement = (e) => ({
  name: e.name || "",
  objective: e.objective || "",
  scenarios: e.scenarios || [],
  rulesOfEngagement: e.rulesOfEngagement || "",
  scope: (e.scope || []).join("\n"),
  exclusions: (e.exclusions || []).join("\n"),
  start: toLocalInput(e.window && e.window.start),
  end: toLocalInput(e.window && e.window.end),
  emergencyContacts: e.emergencyContacts && e.emergencyContacts.length ? e.emergencyContacts.map((c) => ({ ...BLANK_CONTACT, ...c })) : [{ ...BLANK_CONTACT }],
  stopConditions: e.stopConditions || "",
});
const planToPayload = (p) => ({
  name: p.name,
  objective: p.objective,
  scenarios: p.scenarios,
  rulesOfEngagement: p.rulesOfEngagement,
  scope: p.scope,
  exclusions: p.exclusions,
  window: { start: p.start ? new Date(p.start).toISOString() : null, end: p.end ? new Date(p.end).toISOString() : null },
  emergencyContacts: p.emergencyContacts.filter((c) => c.name.trim() || c.phone.trim() || c.email.trim()),
  stopConditions: p.stopConditions,
});

const RedTeam = () => {
  const token = localStorage.getItem("token");
  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "dark" });
  const theme = layoutMode === "light" ? "light" : "dark";
  const apiBase = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";

  const [meta, setMeta] = useState(null);
  const [denied, setDenied] = useState(false);
  const [view, setView] = useState("list"); // list | new | detail
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);

  const [engagement, setEngagement] = useState(null);
  const [missing, setMissing] = useState([]);
  const [tab, setTab] = useState("plan");
  const [plan, setPlan] = useState(EMPTY_PLAN);
  const [editingPlan, setEditingPlan] = useState(false);

  const [activities, setActivities] = useState([]);
  const [actions, setActions] = useState([]);
  const [findings, setFindings] = useState([]);
  const [report, setReport] = useState(null);

  const [decisionNote, setDecisionNote] = useState("");
  const [stopReason, setStopReason] = useState("");
  const [showStop, setShowStop] = useState(false);
  const [lessons, setLessons] = useState("");

  const call = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${apiBase}redteam${path}`, {
        ...options,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(options.headers || {}) },
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
      const body = await res.json().catch(() => ({}));
      return { ok: res.ok, status: res.status, body };
    },
    [apiBase, token]
  );

  const say = useCallback((type, message) => setFeedback({ type, message }), []);

  const loadList = useCallback(async () => {
    setLoading(true);
    const r = await call("/engagements");
    if (r.ok) setItems(r.body.items || []);
    setLoading(false);
  }, [call]);

  useEffect(() => {
    (async () => {
      const r = await call("/meta");
      if (r.status === 403) return setDenied(true);
      if (r.ok) {
        setMeta(r.body);
        loadList();
      }
    })();
  }, [call, loadList]);

  const loadEngagement = useCallback(
    async (id) => {
      const r = await call(`/engagements/${id}`);
      if (!r.ok) return say("danger", r.body.error || "Could not load the engagement.");
      setEngagement(r.body.engagement);
      setMissing(r.body.missingForSubmission || []);
      setPlan(planFromEngagement(r.body.engagement));
      setLessons(r.body.engagement.lessonsLearned || "");
    },
    [call, say]
  );

  const loadTab = useCallback(
    async (id, which) => {
      if (which === "activity" || which === "gaps") {
        const r = await call(`/engagements/${id}/activities`);
        if (r.ok) setActivities(r.body.items || []);
      }
      if (which === "gaps") {
        const r = await call(`/engagements/${id}/actions`);
        if (r.ok) setActions(r.body.items || []);
      }
      if (which === "findings") {
        const r = await call(`/engagements/${id}/findings`);
        if (r.ok) setFindings(r.body.items || []);
      }
      if (which === "report") {
        const r = await call(`/engagements/${id}/report`);
        if (r.ok) setReport(r.body.report);
      }
    },
    [call]
  );

  const engagementId = engagement && engagement._id;
  const engagementStatus = engagement && engagement.status;
  useEffect(() => {
    if (view === "detail" && engagementId) loadTab(engagementId, tab);
  }, [view, tab, engagementId, engagementStatus, loadTab]);

  const openEngagement = async (id) => {
    setFeedback(null);
    setEditingPlan(false);
    setTab("plan");
    setShowStop(false);
    setDecisionNote("");
    setReport(null);
    await loadEngagement(id);
    setView("detail");
  };

  const back = () => {
    setView("list");
    setEngagement(null);
    setFeedback(null);
    loadList();
  };

  // Runs a request, shows the result, then refreshes the engagement
  const act = async (path, options, okMessage, after) => {
    setBusy(true);
    const r = await call(path, options);
    setBusy(false);
    if (!r.ok) {
      say("danger", r.body.error || "That did not work.");
      return r;
    }
    if (okMessage) say("success", okMessage);
    if (engagement) await loadEngagement(engagement._id);
    if (after) after(r);
    return r;
  };

  const savePlan = async () => {
    setBusy(true);
    const payload = planToPayload(plan);
    const r = view === "new" ? await call("/engagements", { method: "POST", body: payload }) : await call(`/engagements/${engagement._id}`, { method: "PATCH", body: payload });
    setBusy(false);
    if (!r.ok) return say("danger", r.body.error || "Could not save the plan.");
    setEditingPlan(false);
    if (view === "new") {
      await openEngagement(r.body.engagement._id);
      say("success", "Draft created. Fill in anything that is missing, then send it for approval.");
    } else {
      await loadEngagement(engagement._id);
      say("success", "Plan saved.");
    }
  };

  const pageClass = `page-content findings-page-root redteam-page`;
  if (denied) {
    return (
      <div className={pageClass} data-theme={theme}>
        <div className="findings-container">
          <div className="findings-main-card" style={{ padding: 32, textAlign: "center", color: "var(--fg-secondary)" }}>
            Your role does not have access to Red Team exercises. Ask your administrator if you need it.
          </div>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className={pageClass} data-theme={theme}>
        <div style={{ textAlign: "center", padding: 60 }}><Spinner color="primary" /></div>
      </div>
    );
  }

  const canWrite = meta.canWrite;
  const canApprove = meta.canApprove;
  const e = engagement;
  const mine = (email) => Boolean(email) && String(email).toLowerCase() === String(meta.me).toLowerCase();
  const editable = view === "new" || (e && e.status === "draft" && editingPlan);
  const ctx = { e, meta, canWrite, canApprove, busy, setBusy, call, say, act, loadTab, activities, actions, findings, report, lessons, setLessons, mine, apiBase, token };

  const setContact = (i, field, value) => setPlan({ ...plan, emergencyContacts: plan.emergencyContacts.map((c, j) => (j === i ? { ...c, [field]: value } : c)) });

  // These two have no state of their own; they are called as functions so typing never remounts them
  const planForm = () => (
    <div className="rt-form">
      <label>Name<input value={plan.name} onChange={(ev) => setPlan({ ...plan, name: ev.target.value })} placeholder="Q4 external perimeter exercise" /></label>
      <label>Objective<textarea rows={2} value={plan.objective} onChange={(ev) => setPlan({ ...plan, objective: ev.target.value })} placeholder="What do we want to learn? For example: how far can an outsider get from the internet?" /></label>
      <div className="rt-field">
        <span className="rt-label">Scenarios</span>
        <div className="rt-checks">
          {meta.scenarios.map((s) => (
            <label key={s.value} className="rt-check">
              <input
                type="checkbox"
                checked={plan.scenarios.includes(s.value)}
                onChange={(ev) => setPlan({ ...plan, scenarios: ev.target.checked ? [...plan.scenarios, s.value] : plan.scenarios.filter((x) => x !== s.value) })}
              />
              {s.label}
            </label>
          ))}
        </div>
      </div>
      <label>Rules of engagement<textarea rows={3} value={plan.rulesOfEngagement} onChange={(ev) => setPlan({ ...plan, rulesOfEngagement: ev.target.value })} placeholder="What is allowed, what is not, working hours, who must be told." /></label>
      <div className="rt-two">
        <label>In scope (one per line: domain, IPv4 address or range like 203.0.113.0/24)<textarea rows={4} value={plan.scope} onChange={(ev) => setPlan({ ...plan, scope: ev.target.value })} placeholder={"example.com\n203.0.113.0/24"} /></label>
        <label>Excluded (never touch)<textarea rows={4} value={plan.exclusions} onChange={(ev) => setPlan({ ...plan, exclusions: ev.target.value })} placeholder="payments.example.com" /></label>
      </div>
      <div className="rt-two">
        <label>Test window starts<input type="datetime-local" value={plan.start} onChange={(ev) => setPlan({ ...plan, start: ev.target.value })} /></label>
        <label>Test window ends (at most 90 days)<input type="datetime-local" value={plan.end} onChange={(ev) => setPlan({ ...plan, end: ev.target.value })} /></label>
      </div>
      <div className="rt-field">
        <span className="rt-label">Emergency contacts (up to 5, each with a phone or an email)</span>
        {plan.emergencyContacts.map((c, i) => (
          <div className="rt-contact" key={i}>
            <input placeholder="Name" value={c.name} onChange={(ev) => setContact(i, "name", ev.target.value)} />
            <input placeholder="Role" value={c.role} onChange={(ev) => setContact(i, "role", ev.target.value)} />
            <input placeholder="Phone" value={c.phone} onChange={(ev) => setContact(i, "phone", ev.target.value)} />
            <input placeholder="Email" value={c.email} onChange={(ev) => setContact(i, "email", ev.target.value)} />
            {plan.emergencyContacts.length > 1 && <button type="button" className="rt-link" onClick={() => setPlan({ ...plan, emergencyContacts: plan.emergencyContacts.filter((_, j) => j !== i) })}>Remove</button>}
          </div>
        ))}
        {plan.emergencyContacts.length < 5 && <button type="button" className="rt-link" onClick={() => setPlan({ ...plan, emergencyContacts: [...plan.emergencyContacts, { ...BLANK_CONTACT }] })}>+ Add a contact</button>}
      </div>
      <label>Stop conditions<textarea rows={2} value={plan.stopConditions} onChange={(ev) => setPlan({ ...plan, stopConditions: ev.target.value })} placeholder="When must the exercise stop immediately? For example: any customer-facing outage." /></label>
      <div className="mgmt-actions">
        <Button color="primary" size="sm" disabled={busy} onClick={savePlan}>{busy ? <Spinner size="sm" /> : view === "new" ? "Create draft" : "Save plan"}</Button>
        <Button color="secondary" outline size="sm" disabled={busy} onClick={() => (view === "new" ? back() : (setEditingPlan(false), setPlan(planFromEngagement(e))))}>Cancel</Button>
      </div>
    </div>
  );

  const planView = () => (
    <div className="rt-stack">
      {e.status === "draft" && missing.length > 0 && <div className="rt-note is-warn">Before this can be sent for approval it still needs: {missing.join(", ")}.</div>}
      <div className="meta-grid">
        <div className="meta-item"><div className="meta-k">Objective</div><div className="meta-v">{e.objective || "—"}</div></div>
        <div className="meta-item"><div className="meta-k">Scenarios</div><div className="meta-v">{(e.scenarios || []).map((s) => (meta.scenarios.find((x) => x.value === s) || {}).label || s).join("; ") || "—"}</div></div>
        <div className="meta-item"><div className="meta-k">Test window</div><div className="meta-v">{fmt(e.window && e.window.start)} to {fmt(e.window && e.window.end)}</div></div>
        <div className="meta-item"><div className="meta-k">Created by</div><div className="meta-v">{e.createdBy}</div></div>
        <div className="meta-item"><div className="meta-k">In scope</div><div className="meta-v">{(e.scope || []).join(", ") || "—"}</div></div>
        <div className="meta-item"><div className="meta-k">Excluded</div><div className="meta-v">{(e.exclusions || []).join(", ") || "None"}</div></div>
      </div>
      <div><div className="drawer-section-title">Rules of engagement</div><div className="desc-box" style={{ whiteSpace: "pre-wrap" }}>{e.rulesOfEngagement || "—"}</div></div>
      <div><div className="drawer-section-title">Stop conditions</div><div className="desc-box" style={{ whiteSpace: "pre-wrap" }}>{e.stopConditions || "—"}</div></div>
      <div>
        <div className="drawer-section-title">Emergency contacts</div>
        {(e.emergencyContacts || []).length ? (
          <div className="rt-list">{e.emergencyContacts.map((c, i) => (<div className="rt-row" key={i}><b>{c.name}</b>{c.role ? ` (${c.role})` : ""}<span className="rt-muted">{[c.phone, c.email].filter(Boolean).join(" · ")}</span></div>))}</div>
        ) : <div className="rt-muted">None yet.</div>}
      </div>
      {e.status === "draft" && canWrite && <div><Button color="primary" outline size="sm" onClick={() => setEditingPlan(true)}>Edit plan</Button></div>}
      <div>
        <div className="drawer-section-title">History</div>
        <div className="history-timeline">
          {(e.history || []).map((h, i) => (
            <div className="timeline-item" key={i}>
              <div className="timeline-header"><span className="timeline-change">{h.from ? `${STATUS_LABELS[h.from] || h.from} → ${STATUS_LABELS[h.to] || h.to}` : `Created as ${STATUS_LABELS[h.to] || h.to}`}</span><span className="timeline-time">{fmt(h.at)}</span></div>
              <div className="timeline-by">By {h.by}</div>
              {h.note && <div className="timeline-note">{h.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const actionBar = () => {
    const hint = e.status === "pending_approval" && mine(e.requestedBy) && !meta.selfApprovalAllowed ? "You sent this for approval, so someone else must approve it." : "";
    const run = (path, okMessage, body) => act(`/engagements/${e._id}${path}`, { method: "POST", body }, okMessage);
    return (
      <div className="rt-actionbar">
        {e.status === "draft" && canWrite && (
          <>
            <Button color="primary" size="sm" disabled={busy || missing.length > 0} onClick={() => run("/submit", "Sent for approval.")} title={missing.length ? `Still needed: ${missing.join(", ")}` : ""}>Send for approval</Button>
            <Button color="secondary" outline size="sm" disabled={busy} onClick={() => run("/cancel", "Engagement cancelled.", {})}>Cancel engagement</Button>
          </>
        )}
        {e.status === "pending_approval" && (
          <>
            {canApprove && (
              <>
                <input className="rt-note-input" placeholder="Note (required to reject)" value={decisionNote} onChange={(ev) => setDecisionNote(ev.target.value)} />
                <Button color="primary" size="sm" disabled={busy} onClick={() => act(`/engagements/${e._id}/decision`, { method: "POST", body: { decision: "approve", note: decisionNote } }, "Engagement approved.", () => setDecisionNote(""))}>Approve</Button>
                <Button color="danger" outline size="sm" disabled={busy} onClick={() => act(`/engagements/${e._id}/decision`, { method: "POST", body: { decision: "reject", note: decisionNote } }, "Engagement sent back to draft.", () => setDecisionNote(""))}>Reject</Button>
              </>
            )}
            {canWrite && <Button color="secondary" outline size="sm" disabled={busy} onClick={() => run("/withdraw", "Withdrawn. You can edit it again.")}>Withdraw</Button>}
            {hint && <span className="rt-muted">{hint}</span>}
          </>
        )}
        {e.status === "approved" && canWrite && (
          <>
            <Button color="primary" size="sm" disabled={busy} onClick={() => run("/start", "The exercise is now active.")}>Start exercise</Button>
            <Button color="secondary" outline size="sm" disabled={busy} onClick={() => run("/withdraw", "Withdrawn. All approvals were cleared.")}>Withdraw to edit</Button>
            <Button color="secondary" outline size="sm" disabled={busy} onClick={() => run("/cancel", "Engagement cancelled.", {})}>Cancel</Button>
          </>
        )}
        {e.status === "active" && canWrite && (
          !showStop ? (
            <>
              <Button color="danger" size="sm" onClick={() => setShowStop(true)}>Emergency stop</Button>
              <Button color="primary" outline size="sm" disabled={busy} onClick={() => run("/complete", "Exercise completed.", { lessonsLearned: lessons })}>Complete exercise</Button>
            </>
          ) : (
            <>
              <input className="rt-note-input" placeholder="Why are you stopping? (required)" value={stopReason} onChange={(ev) => setStopReason(ev.target.value)} />
              <Button color="danger" size="sm" disabled={busy || !stopReason.trim()} onClick={() => act(`/engagements/${e._id}/stop`, { method: "POST", body: { reason: stopReason } }, "The exercise was stopped.", () => { setShowStop(false); setStopReason(""); })}>Confirm stop</Button>
              <Button color="secondary" outline size="sm" onClick={() => setShowStop(false)}>Back</Button>
            </>
          )
        )}
        {e.status === "stopped" && <span className="rt-muted">Stopped by {e.stoppedBy} on {fmt(e.endedAt)}: {e.stopReason}</span>}
      </div>
    );
  };

  return (
    <div className={pageClass} data-theme={theme}>
      <MetaTags><title>Red Team | GuardLogiX</title></MetaTags>
      <div className="findings-container">
        <div className="findings-header">
          <div className="findings-breadcrumbs">
            <Link to="/dashboard">Dashboard</Link>
            <span className="breadcrumb-sep">/</span>
            {view === "list" ? (
              <span className="breadcrumb-active">Red Team</span>
            ) : (
              <>
                <button type="button" className="rt-crumb" onClick={back}>Red Team</button>
                <span className="breadcrumb-sep">/</span>
                <span className="breadcrumb-active">{view === "new" ? "New engagement" : e ? e.name : ""}</span>
              </>
            )}
          </div>
          <div className="findings-title-row">
            <div>
              <h1 className="findings-title">{view === "list" ? "Red Team" : view === "new" ? "New engagement" : e ? e.name : ""}</h1>
              <p className="findings-subtitle">
                {view === "list" ? "Plan authorized red-team exercises, keep the approvals and a record of what was done, and see whether the defenders noticed. Nothing here runs an attack." : e ? <>Status: <Pill status={e.status} /></> : ""}
              </p>
            </div>
            <div className="findings-header-actions">
              {view === "list" && canWrite && <button className="btn-refresh" onClick={() => { setPlan(EMPTY_PLAN); setView("new"); setFeedback(null); }}>New engagement</button>}
              {view !== "list" && <button className="btn-refresh" onClick={back}>← All engagements</button>}
            </div>
          </div>
        </div>

        {feedback && <Alert color={feedback.type} isOpen toggle={() => setFeedback(null)} style={{ borderRadius: "var(--radius-md)", margin: 0 }}>{feedback.message}</Alert>}

        {view === "list" && (
          <div className="findings-main-card">
            <div className="findings-table-wrap">
              <table className="findings-table">
                <thead><tr><th>Engagement</th><th style={{ width: 200 }}>Status</th><th style={{ width: 230 }}>Test window</th><th style={{ width: 90 }}>Scope</th><th style={{ width: 100 }}>Activities</th><th style={{ width: 90 }}>Gaps</th><th style={{ width: 110 }}>Open actions</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" style={{ textAlign: "center", padding: 40 }}><Spinner color="primary" /></td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan="7" style={{ textAlign: "center", padding: "48px 20px" }}>
                      <div style={{ color: "var(--fg-secondary)", fontSize: 14, fontWeight: 600 }}>No engagements yet</div>
                      <div style={{ color: "var(--fg-tertiary)", fontSize: 12, marginTop: 4 }}>{canWrite ? "Press New engagement to plan the first exercise." : "An analyst or administrator can plan one."}</div>
                    </td></tr>
                  ) : (
                    items.map((i) => (
                      <tr key={i._id} onClick={() => openEngagement(i._id)} style={{ cursor: "pointer" }}>
                        <td><div style={{ fontWeight: 600, color: "var(--fg-primary)" }}>{i.name}</div><div className="rt-muted">{i.objective ? i.objective.slice(0, 90) : "No objective yet"}</div></td>
                        <td><Pill status={i.status} />{i.pendingApprovals > 0 && <span className="rt-badge is-pending">{i.pendingApprovals} approval{i.pendingApprovals === 1 ? "" : "s"} waiting</span>}</td>
                        <td className="rt-small">{i.window && i.window.start ? `${fmt(i.window.start, false)} to ${fmt(i.window.end, false)}` : "—"}</td>
                        <td>{(i.scope || []).length}</td>
                        <td>{i.activities}</td>
                        <td>{i.gaps > 0 ? <span className="rt-badge is-not_detected">{i.gaps}</span> : 0}</td>
                        <td>{i.openActions}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === "new" && <div className="findings-main-card rt-pad">{planForm()}</div>}

        {view === "detail" && e && (
          <div className="findings-main-card rt-pad">
            {actionBar()}
            <div className="rt-tabs">
              {TABS.map(([key, label]) => (<button key={key} type="button" className={tab === key ? "active" : ""} onClick={() => setTab(key)}>{label}</button>))}
            </div>
            {tab === "plan" && (editable ? planForm() : planView())}
            {tab === "approvals" && <ApprovalsTab ctx={ctx} />}
            {tab === "activity" && <ActivityTab ctx={ctx} />}
            {tab === "gaps" && <GapsTab ctx={ctx} />}
            {tab === "findings" && <FindingsTab ctx={ctx} />}
            {tab === "report" && <ReportTab ctx={ctx} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default RedTeam;
